import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { acct, fmtDate, kes } from "@/lib/money";
import { Page, Btn, Table, Tr, Td, Pill, Loading } from "@/components/kit";
import { download } from "@/lib/download";

const HOW = { reference: "By reference", amount: "By amount", together: "Together", posted: "Posted from statement", running: "Running total with the shifts", rubis_card_open: "Rubis Card - running total (open)", mpesa_open: "M-Pesa withdrawals - running total (open)" };
// [field, label, the statement's side, the shifts' side] - settled later, matched as running totals
const RUNNING_KINDS = [["rubis_card", "Rubis Card", "credited by Rubis", "shift sales"],
  ["mpesa", "M-Pesa withdrawals", "received by Rubis", "withdrawn on the shifts"]];
const input = "min-h-[36px] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800";

export default function Statements() {
  const { company } = useCompany();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState(null);
  const aid = params.get("account") || "";
  useEffect(() => {
    if (!company) return;
    api.get(`/companies/${company.id}/statements`).then(({ data }) => {
      setList(data);
      if (!aid && data.length) setParams({ account: (data.find((a) => a.lines) || data[0]).id }, { replace: true });
    });
  }, [company]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list) return <Page title="Statements"><Loading /></Page>;
  return (
    <Page title="Statements">
      <p className="mt-2 max-w-3xl text-sm text-[#5F5C58]">
        Upload a statement - Rubis (PDF), bank or M-Pesa (CSV or Excel) - and each line is matched to the books. What's left on
        either side is what to look at. Uploading overlapping statements is safe: lines already in are skipped.
      </p>
      <div className="mt-4 flex flex-wrap gap-2" role="tablist">
        {list.map((a) => (
          <button key={a.id} type="button" role="tab" aria-selected={a.id === aid} onClick={() => setParams({ account: a.id })}
            className={`rounded-full border px-3 py-1.5 text-sm ${a.id === aid ? "border-brand bg-[#F3E6E6] font-semibold text-brand" : "border-[#E4E2DE] bg-white hover:bg-[#F6F5F3]"}`}>
            {a.code} {a.name}{a.lines ? "" : " ·"}
          </button>
        ))}
      </div>
      {aid && <Reconciliation key={aid} cid={company.id} aid={aid} />}
    </Page>
  );
}

function Reconciliation({ cid, aid }) {
  const [data, setData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const load = useCallback(async () => setData((await api.get(`/companies/${cid}/statements/${aid}`)).data), [cid, aid]);
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/companies/${cid}/accounts`).then(({ data: a }) => setAccounts(a.filter((x) => x.active !== false && x.id !== aid)));
  }, [load, cid, aid]);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = (await api.post(`/companies/${cid}/statements/${aid}/upload`, form)).data;
      toast.success(`${r.added} new lines${r.already_in ? ` (${r.already_in} were already in)` : ""} · ${r.format}`);
      Object.entries(r.station_credits || {}).forEach(([station, s]) => (s.error
        ? toast.error(`${station}: credit notes not recorded - upload the statement again (${s.error})`)
        : toast.success(`${station}: ${s.added} Rubis credit note${s.added === 1 ? "" : "s"} recorded${s.already_in ? `, ${s.already_in} already there` : ""}`)));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      if (file.current) file.current.value = "";
    }
  };
  const act = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  if (!data) return <Loading />;
  const d = data;
  return (
    <>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{d.account.code} {d.account.name}{d.account.statement_account_no ? ` · statement account ${d.account.statement_account_no}` : ""}</h2>
        <input ref={file} type="file" accept=".pdf,.csv,.xlsx,text/csv,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <Btn kind="primary" disabled={busy} onClick={() => file.current?.click()}>{busy ? "Uploading…" : "Upload statement"}</Btn>
      </div>
      {d.to_date ? (
        <div className="mt-3 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Stat label={`Statement at ${fmtDate(d.to_date)}`} value={d.statement_balance} />
          <Stat label={`Books at ${fmtDate(d.to_date)}`} value={d.book_balance} />
          <Stat label="Difference" value={d.difference} tone={d.difference == null ? "" : Math.abs(d.difference) < 1 ? "text-emerald-700" : "text-[#A11D1D]"}
            note={d.difference != null && Math.abs(d.difference) < 1 ? "Agrees" : "Explained by the lines below"} />
          {d.statement_note && <p className="col-span-full text-xs text-[#5F5C58]">{d.statement_note}</p>}
        </div>
      ) : <p className="mt-6 text-sm text-[#5F5C58]">No statement uploaded for this account yet.</p>}

      {RUNNING_KINDS.filter(([k]) => d[k]).map(([k, label, theirs, ours]) => (
        <section key={k} className="mt-6 rounded-[10px] border border-[#E4E2DE] bg-white p-4 text-sm">
          <h3 className="text-[15px] font-semibold">{label} - running total since {fmtDate(d[k].from)}</h3>
          <p className="mt-1 text-[#5F5C58]">Matched as running totals, not line by line. Don't post these statement lines - they're already in the books from the shifts.</p>
          <div className="mt-2 flex flex-wrap gap-x-8 gap-y-1">
            <span>{ours[0].toUpperCase() + ours.slice(1)}{d[k].last_shift ? ` to ${fmtDate(d[k].last_shift)}` : ""}: <b>{kes(d[k].sales_all)}</b></span>
            <span>{theirs[0].toUpperCase() + theirs.slice(1)}: <b>{kes(d[k].credited_all)}</b></span>
            <span>Awaiting: <b className={d[k].awaiting < -1 ? "text-[#A11D1D]" : ""}>{kes(d[k].awaiting)}</b>{d[k].awaiting < -1 ? " (the statement shows more than the shifts recorded - check the shifts)" : ""}</span>
          </div>
        </section>
      ))}
      {d.to_date && d.difference != null && Math.abs(d.difference) >= 1 && <WhyDifferent d={d} />}

      {d.differences.length > 0 && (<>
        <h3 className="mt-8 text-[15px] font-semibold">Matched, but the amount differs</h3>
        <Table minWidth={720} cols={[{ label: "Date" }, { label: "Ref" }, { label: "Statement" }, { label: "Statement", align: "right" }, { label: "Books", align: "right" }, { label: "Difference", align: "right" }]}>
          {d.differences.map((s) => (
            <Tr key={s.id}><Td>{fmtDate(s.date)}</Td><Td>{s.ref}</Td><Td className="max-w-[320px] text-[#5F5C58]">{s.kind} {s.description}</Td>
              <Td right>{acct(s.amount)}</Td><Td right>{acct(s.amount - s.match.difference)}</Td><Td right className="font-semibold text-[#A11D1D]">{acct(s.match.difference)}</Td></Tr>
          ))}
        </Table>
      </>)}

      <h3 className="mt-8 text-[15px] font-semibold">On the statement, not in the books</h3>
      {d.not_in_books.length === 0 ? <p className="py-3 text-sm text-[#5F5C58]">Nothing</p> : (
        <Table minWidth={820} cols={[{ label: "Date" }, { label: "Ref" }, { label: "Description" }, { label: "Amount", align: "right" }, { label: "" }]}>
          {d.not_in_books.map((s) => <OpenLine key={s.id} s={s} accounts={accounts} cid={cid} aid={aid} act={act} />)}
        </Table>
      )}

      <h3 className="mt-8 text-[15px] font-semibold">In the books, not on the statement yet</h3>
      {d.not_on_statement.length === 0 ? <p className="py-3 text-sm text-[#5F5C58]">Nothing</p> : (
        <Table minWidth={680} cols={[{ label: "Date" }, { label: "Journal" }, { label: "Line" }, { label: "Amount", align: "right" }]}>
          {d.not_on_statement.map((b) => (
            <Tr key={b.id}><Td>{fmtDate(b.date)}</Td><Td>{b.journal_memo}{b.ref ? <span className="block text-xs text-[#5F5C58]">{b.ref}</span> : null}</Td><Td className="text-[#5F5C58]">{b.memo}</Td><Td right>{acct(b.amount)}</Td></Tr>
          ))}
        </Table>
      )}

      {d.account.key === "rubis_fuel" && <RubisCard cid={cid} aid={aid} />}

      <h3 className="mt-8 text-[15px] font-semibold">Every statement line</h3>
      <Table minWidth={820} cols={[{ label: "Date" }, { label: "Ref" }, { label: "Description" }, { label: "Amount", align: "right" }, { label: "Matched" }]}>
        {d.statement.map((s) => (
          <Tr key={s.id} className={s.status === "ignored" ? "opacity-60" : ""}>
            <Td>{fmtDate(s.date)}</Td><Td>{s.ref}</Td><Td className="max-w-[360px] text-[#5F5C58]">{s.kind} {s.description}</Td><Td right>{acct(s.amount)}</Td>
            <Td>{s.match ? <Pill kind={s.match.difference ? "amber" : "green"}>{HOW[s.match.how]}</Pill>
              : s.status === "ignored" ? <span className="text-xs">Ignored · <button type="button" className="text-sky-700 hover:underline" onClick={() => act(() => api.post(`/companies/${cid}/statements/${aid}/lines/${s.id}/status`, { status: "open" }), "Back on the list")}>undo</button></span>
                : <Pill kind="red">Not in books</Pill>}</Td>
          </Tr>
        ))}
      </Table>
    </>
  );
}

function RubisCard({ cid, aid }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/companies/${cid}/statements/${aid}/rubis-card`).then(({ data }) => setD(data)); }, [cid, aid]);
  if (!d) return null;
  return (
    <section className="mt-8 rounded-[10px] border border-[#E4E2DE] bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-[15px] font-semibold">Rubis Card & App: sales and Rubis credits</h3>
        <Btn onClick={() => download(`/companies/${cid}/statements/${aid}/rubis-card/excel`)}>Download Excel</Btn>
      </div>
      <p className="mt-1 text-sm text-[#5F5C58]">The shifts' Rubis Card and App sales against Rubis's credit notes, day by day{d.statement_to ? `, up to the statement of ${fmtDate(d.statement_to)}` : ""}. Rubis credits by calendar day and the shifts run overnight, so follow the running balance.</p>
      <div className="mt-3 flex flex-wrap gap-6 text-sm">
        <span>Debits (shift sales) <b className="font-mono">{kes(d.summary.sales)}</b></span>
        <span>Credits (Rubis credit notes) <b className="font-mono">{kes(d.summary.credited)}</b></span>
        <span className={d.summary.awaiting < -1 ? "text-[#A11D1D]" : ""}>Balance awaiting credit <b className="font-mono">{kes(d.summary.awaiting)}</b></span>
      </div>
      <Table minWidth={720} cols={[{ label: "Date" }, { label: "Debit - shift sales", align: "right" }, { label: "Credit - Rubis credit notes", align: "right" }, { label: "Balance awaiting credit", align: "right" }]}>
        {d.days.map((x) => (
          <Tr key={x.date}>
            <Td>{fmtDate(x.date)}</Td>
            <Td right>{x.sales ? kes(x.sales) : "–"}{x.shifts.map((t, k) => <span key={k} className="block text-xs text-[#5F5C58]">{t}</span>)}</Td>
            <Td right>{x.credited ? kes(x.credited) : "–"}{x.credits.map((t, k) => <span key={k} className="block text-xs text-[#5F5C58]">{t}</span>)}</Td>
            <Td right className={`font-semibold ${x.awaiting < -1 ? "text-[#A11D1D]" : ""}`}>{kes(x.awaiting)}</Td>
          </Tr>
        ))}
      </Table>
    </section>
  );
}

function Stat({ label, value, tone = "", note }) {
  return (
    <section className="rounded-[10px] border border-[#E4E2DE] bg-white p-4">
      <div className="text-xs font-semibold uppercase text-[#5F5C58]">{label}</div>
      <div className={`mt-1 font-mono text-2xl tabular-nums ${tone}`}>{value == null ? "–" : kes(value)}</div>
      {note && <div className="text-xs text-[#5F5C58]">{note}</div>}
    </section>
  );
}

function OpenLine({ s, accounts, cid, aid, act }) {
  const [to, setTo] = useState("");
  const [memo, setMemo] = useState("");
  return (
    <Tr>
      <Td>{fmtDate(s.date)}</Td><Td>{s.ref}</Td><Td className="max-w-[320px] text-[#5F5C58]">{s.kind} {s.description}</Td><Td right>{acct(s.amount)}</Td>
      <Td>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <select aria-label="Post against" className={input} value={to} onChange={(e) => setTo(e.target.value)}>
            <option value="">Post against…</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </select>
          <input aria-label="Note" className={`${input} w-40`} placeholder="Note (optional)" value={memo} onChange={(e) => setMemo(e.target.value)} />
          <Btn kind="primary" disabled={!to} onClick={() => act(() => api.post(`/companies/${cid}/statements/${aid}/lines/${s.id}/post`, { account_id: to, memo }), "Posted")}>Post</Btn>
          <Btn onClick={() => act(() => api.post(`/companies/${cid}/statements/${aid}/lines/${s.id}/status`, { status: "ignored" }), "Ignored")}>Ignore</Btn>
        </div>
      </Td>
    </Tr>
  );
}

// statement minus books, item by item: a line only on the statement adds its amount, a line only in the
// books takes its amount off, a matched line with a different amount adds the gap - and what's left over
function WhyDifferent({ d }) {
  const rows = [
    ...d.not_in_books.map((s) => ({ key: `s${s.id}`, date: s.date, what: `On the statement, not in the books: ${s.kind} ${s.ref} ${s.description || ""}`, amount: s.amount })),
    ...d.not_on_statement.map((b) => ({ key: `b${b.id}`, date: b.date, what: `In the books, not on the statement yet: ${b.journal_memo} (${b.memo})`, amount: -b.amount })),
    ...d.differences.map((s) => ({ key: `d${s.id}`, date: s.date, what: `${s.kind} ${s.ref}: statement ${acct(s.amount)}, books ${acct(s.amount - s.match.difference)}`, amount: s.match.difference })),
    ...RUNNING_KINDS.filter(([k]) => d[k] && Math.abs(d[k].timing) >= 0.01).map(([k, label, theirs, ours]) => ({ key: k, date: d.to_date,
      what: `${label} timing: ${theirs} ${acct(d[k].credited)}, ${ours} in the books ${acct(d[k].sales)} (to ${fmtDate(d.to_date)})`,
      amount: d[k].timing })),
  ].sort((a, b) => (a.date < b.date ? -1 : 1));
  const explained = rows.reduce((a, r) => a + r.amount, 0);
  const left = Math.round((d.difference - explained) * 100) / 100;
  return (
    <section className="mt-6 rounded-[10px] border border-[#E4E2DE] bg-white p-4">
      <h3 className="text-[15px] font-semibold">Why they differ: statement {acct(d.statement_balance)} less books {acct(d.book_balance)} = {acct(d.difference)}</h3>
      <Table minWidth={680} cols={[{ label: "Date" }, { label: "Item" }, { label: "Adds to the difference", align: "right" }]}>
        {rows.map((r) => <Tr key={r.key}><Td>{fmtDate(r.date)}</Td><Td className="text-[#5F5C58]">{r.what}</Td><Td right>{acct(r.amount)}</Td></Tr>)}
        {Math.abs(left) >= 0.01 && <Tr><Td /><Td className="text-[#A11D1D]">Not explained by the lines (an opening balance or a line dated outside the statement)</Td><Td right className="text-[#A11D1D]">{acct(left)}</Td></Tr>}
        <Tr><Td /><Td className="font-semibold">Difference</Td><Td right className="font-semibold">{acct(d.difference)}</Td></Tr>
      </Table>
    </section>
  );
}
